<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '620px'"
		:persistent="confirmDeleteKeyButtonLoading"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
	>
		<v-card
			class="sgnl-pb-0"
			data-test="delete-api-key-dialog-sheet"
		>
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>Delete API Key
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<div class="delete-key-body__intro">
					<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
						After deleting the key, the Signaloid Cloud Compute Engine API will reject incoming requests
						that try to use it.
					</div>
					<SgnlTable
						:columns="columns"
						:data="rows"
						empty-text="Could not display key."
						:card-breakpoint="600"
					>
						<template #cell-Name="{ row }">
							<div
								class="key-name sgnl-paragraph-s-regular sgnl-text-color-default"
								:title="row.Name"
							>
								{{ row.Name }}
							</div>
						</template>

						<template #cell-KeyID>
							<span class="sgnl-code-default sgnl-text-color-secondary">{{ formattedKey }}...</span>
						</template>

						<template #cell-FormattedCreatedAt="{ row }">
							<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary">{{
								row.FormattedCreatedAt
							}}</span>
						</template>

						<template #cell-FormattedValidUntil="{ row }">
							<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary">{{
								row.FormattedValidUntil
							}}</span>
						</template>

						<!-- Mobile card -->
						<template #card="{ row }">
							<div
								class="sgnl-paragraph-s-medium sgnl-text-color-default"
								:title="row.Name"
							>
								{{ row.Name }}
							</div>
							<div class="sgnl-code-default sgnl-text-color-secondary">{{ formattedKey }}...</div>
							<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
								Created {{ row.FormattedCreatedAt }}
							</div>
							<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
								Valid until {{ row.FormattedValidUntil }}
							</div>
						</template>
					</SgnlTable>
				</div>
				<AlertMessage type="error">
					<strong>Attention!</strong> Applications using this API key to make requests to the Signaloid Cloud
					Compute Engine <strong>may stop working!</strong>
				</AlertMessage>
				<AlertMessage
					v-if="errorText !== ''"
					type="error"
				>
					{{ errorText }}
				</AlertMessage>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
				>
					Cancel
				</v-btn>
				<v-btn
					variant="flat"
					color="error"
					@click="confirmDeleteKey"
					data-test="delete-api-key-button"
					:loading="confirmDeleteKeyButtonLoading"
				>
					Confirm Deletion
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { PropType, defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";

// Types
import { UserKeyFormatted } from "@/types/api/keys";

// Libraries
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";

// Utilities
import * as Sentry from "@sentry/vue";

// Components
import SgnlTable from "@/components/Common/Table.vue";
import AlertMessage from "@/components/Common/AlertMessage.vue";
import { type ColumnDef } from "@tanstack/vue-table";

// Stores
import { useUserStore } from "@/stores/user";

// Single read-only row; no sorting or pagination needed.
const KEY_COLUMNS: ColumnDef<UserKeyFormatted>[] = [
	{ accessorKey: "Name", header: "Name", enableSorting: false },
	{ id: "KeyID", header: "Key ID", enableSorting: false },
	{ accessorKey: "FormattedCreatedAt", header: "Creation Date", enableSorting: false },
	{ accessorKey: "FormattedValidUntil", header: "Valid Until", enableSorting: false },
];

type ComponentData = {
	confirmDeleteKeyButtonLoading: boolean;
	errorText: string;
};

function formatKey(keyID: string): string {
	return "scce_" + keyID.substring(0, 8);
}

export default defineComponent({
	name: "DeleteAPIKey",
	components: { Close16, SgnlTable, AlertMessage },
	emits: ["update:showDialog", "keyDeleted"],
	data: (): ComponentData => ({
		confirmDeleteKeyButtonLoading: false,
		errorText: "",
	}),
	props: {
		showDialog: { type: Boolean, default: false },
		passedKey: {
			type: Object as PropType<UserKeyFormatted>,
			default: () => {},
		},
	},
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		formattedKey(): string {
			return formatKey(this.passedKey.KeyID);
		},
		columns(): ColumnDef<UserKeyFormatted>[] {
			return KEY_COLUMNS;
		},
		rows(): UserKeyFormatted[] {
			return this.passedKey?.KeyID ? [this.passedKey] : [];
		},
	},
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	methods: {
		handleDismiss() {
			if (this.confirmDeleteKeyButtonLoading) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.$emit("update:showDialog", false);
		},
		async confirmDeleteKey() {
			this.confirmDeleteKeyButtonLoading = true;
			this.errorText = "";
			const keyID = (this.passedKey as UserKeyFormatted).KeyID;

			try {
				await sdk.keys.delete(keyID);
				this.$posthog?.capture("api_key_deleted");
				this.closeDialog();
				this.$emit("keyDeleted", keyID);
			} catch (error) {
				// Can fail with: 400, 404, 500
				let severityLevel: Sentry.SeverityLevel = "error";
				if (error instanceof SdkError) {
					/*
					 *	This API can only return: 400, 404, 500, 401, 404 (UserID), 500
					 */
					switch (error.code) {
						case ERROR_CODES.API_SERVER_ERROR:
							severityLevel = "fatal"; // HTTP 500 means a Lambda is failing!
							this.errorText =
								`Our system encountered a server error deleting the API key.` +
								" Please try refreshing the page. If this error persists, please contact support at developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_UNAUTHORIZED:
							this.errorText =
								"Our system encountered an authorization error while deleting the API key." +
								" Please try refreshing the page. Signing out and back in will ensure you are properly authorized." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_NOT_FOUND:
						case ERROR_CODES.API_BAD_REQUEST:
							severityLevel = "fatal"; // Why is it even in the list if it doesn't exist?
							this.errorText =
								"Our system encountered an error while deleting this API key." +
								" Our team will receive a high-priority notification for this issue." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;

						default:
							break;
					}
				} else {
					// Network / other error (no response)
					this.errorText =
						"A request to delete the API key was initiated but there was no response." +
						" Please ensure that you have an active Internet connection." +
						" If this error persists, please contact support at developer-support@signaloid.com.";
				}
				console.log(error);
				Sentry.captureException(error, {
					level: severityLevel,
					extra: { userMessage: this.errorText },
					tags: { service: "delete-api-key" },
				});
				console.log(error);
			} finally {
				this.confirmDeleteKeyButtonLoading = false;
			}
		},
	},
});
</script>

<style lang="css" scoped>
.delete-key-body__intro {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.key-name {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 120px;
}

/* Give the key its own room so the truncated value stays on one line. */
:deep(.col-Name) {
	width: 22%;
}

:deep(.col-KeyID) {
	width: 30%;
}

:deep(.col-FormattedCreatedAt) {
	width: 24%;
}

:deep(.col-FormattedValidUntil) {
	width: 24%;
}
</style>
